import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'I only have a couple of properties. Is this overkill?',
    answer:
      'The opposite — landlords with one or two properties are exactly who Resida is for. You don\'t have a property management team, so every follow-up, every receipt, and every lease renewal is on you. Resida keeps it organized so things don\'t slip through the cracks.',
  },
  {
    question: 'There are free tools out there. Why would I pay?',
    answer:
      'Free tools cover the basics — listing a property, collecting rent. But you end up stitching together three or four apps, manually tracking expenses, and piecing together records at tax time. Resida puts everything in one place and does the expense categorization for you. The free trial lets you see if the time savings are worth it before you pay anything.',
  },
  {
    question: 'How does the AI expense tracking work?',
    answer:
      'Take a photo of a receipt or upload an image. Resida reads the vendor name, amount, and date, then suggests which IRS Schedule E category it belongs to (repairs, insurance, utilities, etc.). You review the suggestion and confirm. When tax season comes, your expenses are already categorized and ready to export.',
  },
  {
    question: 'Do my tenants need to install anything?',
    answer:
      'No. Tenants use a web portal that works on any device — phone, tablet, or computer. They can pay rent, submit maintenance requests with photos, and review their lease. Nothing to download.',
  },
  {
    question: 'Is my financial data secure?',
    answer:
      'Yes. Payment processing uses industry-standard encryption. Resida never stores raw card or bank account numbers. All lease, communication, and financial data is accessible only to authorized users on your account.',
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'Tenants can pay rent via ACH bank transfer or Zelle. Payments are tracked automatically so you always know who has paid and who hasn\'t — no manual reconciliation needed.',
  },
  {
    question: 'Can I cancel anytime?',
    answer:
      'Yes. There are no long-term contracts or cancellation fees. You can cancel your subscription at any time. Your data remains accessible through the end of your billing period.',
  },
];

export function FAQ(): JSX.Element {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section
      id="faq"
      data-testid="landing-faq"
      className="py-[var(--landing-section-py)] bg-[var(--landing-surface-alt)]"
    >
      <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--landing-text-primary)] mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-[var(--landing-text-muted)] max-w-2xl mx-auto">
            Common questions from independent landlords considering Resida.
          </p>
        </div>
        <div className="max-w-3xl mx-auto">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className="border-b border-[var(--landing-border)]">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full text-left py-4 flex items-center justify-between min-h-[44px] text-[var(--landing-text-primary)] hover:text-[var(--landing-accent-text)] transition-colors"
                data-testid={`faq-item-${i}`}
                aria-expanded={openIndex === i}
              >
                <span className="font-medium pr-4">{item.question}</span>
                <span
                  className="text-xl font-light flex-shrink-0 text-[var(--landing-accent-text)]"
                  aria-hidden="true"
                >
                  {openIndex === i ? '−' : '+'}
                </span>
              </button>
              {openIndex === i && (
                <div
                  className="pb-4 text-[var(--landing-text-muted)] leading-relaxed"
                  data-testid={`faq-answer-${i}`}
                >
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
